<template>
  <div>
    <div
      v-if="
        (!props.endpoint ||
          (props.endpoint && props.endpoint.includes('undefined'))) &&
        (auxInputType == null || auxInputType === '') &&
        !props.options
      "
    >
      <PrimeSelect
        placeholder="Incorrectly configured..."
        class="p-invalid w-full"
        :disabled="true"
      />
    </div>

    <PrimeSelect
      v-model="dropdownValue"
      :options="validatedOptions"
      :filter="props.filterEnabled"
      @show="
        () => {
          if (isInitialLoad) {
            refreshOptionList();
          }
        }
      "
      :filterFields="
        validatedOptions.length > 0 ? Object.keys(validatedOptions[0]) : []
      "
      @filter="
        ($event: any) => {
          debouncedSetSearch($event.value);
        }
      "
      class="w-full"
      @change="
        ($event: any) => {
          changeEventHandler($event);
        }
      "
      :autoFilterFocus="true"
      :optionLabel="
        auxInputType && auxOptionComponentExists ? undefined : props.displayKey
      "
      :optionValue="props.valueKey"
      :showClear="props.required != true"
      v-on:hide="blurEventHandler($event)"
      ref="selectDropdown"
      v-on:blur="blurEventHandler($event)"
      :disabled="pending || props.disabled"
      :invalid="hasErrors"
      :placeholder="props.placeholder"
      :editable="props.editable"
      :loading="pending"
      v-else
    >
      <template #footer>
        <div class="italic font-normal w-full p-2 flex flex-row justify-end">
          <div>Type for more values</div>
        </div>
      </template>
      <template
        #value
        v-if="auxSelectedComponent && auxSelectedComponentExists"
      >
        <component
          :is="auxSelectedComponent"
          :data="currentValueItem"
          v-if="currentValueItem"
        ></component>
      </template>
      <template
        #option="{ option }"
        v-if="auxInputType && auxOptionComponentExists"
      >
        <component :is="auxOptionComponent" :data="option"></component>
      </template>
    </PrimeSelect>
  </div>
  <!-- <pre>{{ options.data }}</pre> -->
</template>

<script setup lang="ts">
import { isEqual } from "radash";
import {
  type IInputEvent,
  type IChangeEvent,
  type IBlurEvent,
} from "~/scad-shared/types/interfaces";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    inputType?: string;
    error?: string;
    loading?: boolean;
    id?: string;
    options?: any[];
    fetchOptions?: boolean;
    auxInputType?: string;
    editable?: boolean;
    endpoint?: string;
    displayKey?: string;
    searchType?: string;
    filterQueryType?: "normal" | "serialized" | "both";
    valueKey?: string;
    responseKey?: string;
    limit?: number;
    filterEnabled?: boolean;
    filter?: { [key: string]: any };
    tokenData?: { [key: string]: any };
    placeholder?: string;
    defaultExpression?: string;
    dataKey?: string;
    affectFields?: string[];
    showAllOptionInSelect?: boolean;
    required?: boolean;
  }>(),
  {
    disabled: false,
    editable: false,
    filterQueryType: "normal",
    dataKey: Math.random().toString().substring(2, 20),
    loading: false,
    fetchOptions: false,
    responseKey: "Results",
    displayKey: "Name",
    valueKey: "Value",
    limit: 40,
    filterEnabled: true,
    tokenData: () => ({}),
    filter: () => ({}),
    affectFields: () => [],
  },
);

const filter = ref(props.filter);
watch(
  () => props.filter,
  (newValue, oldValue) => {
    if (!isEqual(newValue, oldValue)) {
      filter.value = newValue;
    }
  },
  { deep: true },
);
const value = defineModel<any>("value");
const dropdownValue = ref<any>();
const form = defineModel<any>("form", {
  default: () => ({}),
});
const actualValue = defineModel<any>("actual");

const { user } = useAuthStore();

const processedEndpoint = computed(() => {
  return props.endpoint
    ? processEndpoint(props.endpoint, {
        ...(form.value ?? {}),
        ...props.tokenData,
        meta_session_UserId: user?.UserId,
        meta_session_UserName: user?.UserName,
      })
    : null;
});

const isInitialLoad = ref(true);
const hasSetDefaults = ref(false);

function checkDefaultValues(options: any[]) {
  if (
    props.defaultExpression &&
    isInitialLoad.value &&
    !hasSetDefaults.value &&
    options.length > 0 &&
    !value.value
  ) {
    hasSetDefaults.value = true;
    const parts = props.defaultExpression.split("=");
    switch (parts[0]) {
      case "@first":
        value.value = options[0][props.valueKey];
        actualValue.value = options[0][props.displayKey];
        dropdownValue.value = options[0][props.valueKey];
        break;
      case "@last":
        value.value = options[options.length - 1][props.valueKey];
        actualValue.value = options[options.length - 1][props.displayKey];
        dropdownValue.value = options[options.length - 1][props.valueKey];
        break;
      case "@value":
        try {
          const valueToSet = parts[1];
          const option = options.find(
            (option) => option[props.displayKey] == valueToSet,
          );
          if (option) {
            value.value = option[props.valueKey];
            actualValue.value = option[props.displayKey];
            dropdownValue.value = option[props.valueKey];
          }
        } catch (error) {
          // Error setting default value
        }
        break;
      default:
        // example is '$0' which selects the first item with a value of '0'
        if (parts[0].startsWith("$")) {
          const valueToSelect = parts[0].slice(1);
          const option = options.find(
            (option) => option[props.valueKey] == valueToSelect,
          );
          if (option) {
            value.value = option[props.valueKey];
            actualValue.value = option[props.displayKey];
            dropdownValue.value = option[props.valueKey];
          }
        }

        break;
    }
  }
}

const search = ref<string>();

const debouncedSetSearch = useDebounceFn((value: string) => {
  search.value = value;
}, 300);

function setAffectedValues(data: any) {
  props.affectFields.forEach((valueKey: string) => {
    // remove square brackets from valueKey if they exist
    valueKey = valueKey.replace(/\[|\]/g, "");

    const keys = valueKey.split("@");
    const from = keys[0];
    const to = keys.length > 1 ? keys[1] : from;
    if (from.charAt(0) == "#") {
      // Set to value in form to static string
      form.value[to] = from.slice(1);
    } else if (from.charAt(0) == "$") {
      // Set to value in form to static number
      form.value[to] = Number(from.slice(1));
    } else if (from.includes("?")) {
      // Set to value in form to value depending on expression
      // TODO: Implement
    } else if (from.charAt(0) == "!") {
      if (from.slice(1) in data) {
        form.value[to] = data[from.slice(1)];
      } else {
        form.value[to] = null;
      }
    } else {
      if (from in data) {
        form.value[to] = data[from];
      }
    }
  });
}

const {
  data: optionList,
  error: optionListError,
  status: optionListStatus,
  refresh: refreshOptionList,
} = useFetch(processedEndpoint.value ?? "", {
  query: {
    take: 40,
  },
  watch: [search, filter],
  immediate: false,
  server: false,
  lazy: true,
  onRequest({ request, options: requestOptions }) {
    const searchQuery: { [key: string]: string } = {};

    if (search.value && search.value !== "") {
      if (props.searchType?.toUpperCase() == "EXACT") {
        searchQuery[props.displayKey] = search.value;
      } else {
        searchQuery[`${props.displayKey}Contains`] = search.value;
      }
    }

    const formattedFilter: { [key: string]: string } = {};
    // Format props.filter to handle arrays correctly
    Object.keys(props.filter).forEach((key) => {
      const value = props.filter[key];
      if (Array.isArray(value)) {
        formattedFilter[key.endsWith("Id") ? key + "s" : key] = value.join(",");
      } else {
        formattedFilter[key] = value;
      }
    });
    switch (props.filterQueryType) {
      case "serialized":
        requestOptions.query = {
          ...requestOptions.query,
          Filters: JSON.stringify({
            ...searchQuery,
            ...formattedFilter,
          }),
        };
        break;
      case "both":
        requestOptions.query = {
          ...requestOptions.query,
          ...searchQuery,
          ...formattedFilter,
          Filters: JSON.stringify({
            ...searchQuery,
            ...formattedFilter,
          }),
        };
        break;
      case "normal":
      default:
        requestOptions.query = {
          ...requestOptions.query,
          ...searchQuery,
          ...formattedFilter,
        };
        break;
    }
  },
  transform(res: any) {
    if (res && props.responseKey && res[props.responseKey]) {
      const values = props.responseKey
        .trim()
        .split(".")
        .reduce((a, b) => a[b], res);

      // Validate the response is an array and has proper structure
      if (Array.isArray(values)) {
        return values.filter((item) => {
          return item && item[props.valueKey] != null;
        });
      }
    } else if (res && !props.responseKey) {
      // If no responseKey provided, assume entire response is the array
      if (Array.isArray(res)) {
        return res.filter((item) => {
          return item && item[props.valueKey] != null;
        });
      }
    }
    return [];
  },
});

const {
  data: currentDataItem,
  error: currentDataItemError,
  status: currentDataItemStatus,
  refresh: refreshCurrentDataItem,
} = useFetch(processedEndpoint.value ?? "", {
  query: {
    take: 1,
  },
  server: false,
  lazy: true,
  onRequest({ request, options: requestOptions }) {
    const searchQuery: { [key: string]: string } = {};

    if (value.value) {
      if (Array.isArray(value.value)) {
        searchQuery[props.valueKey + "s"] = value.value.join(","); // Converts the array to a comma-separated string
      } else {
        searchQuery[props.valueKey] = value.value;
      }
    }

    const formattedFilter: { [key: string]: string } = {};

    // Format props.filter to handle arrays correctly
    Object.keys(props.filter).forEach((key) => {
      const value = props.filter[key];
      if (Array.isArray(value)) {
        formattedFilter[key.endsWith("Id") ? key + "s" : key] = value.join(",");
      } else {
        formattedFilter[key] = value;
      }
    });

    requestOptions.query = {
      ...requestOptions.query,
      ...searchQuery,
      ...formattedFilter,
    };
  },
  transform(res: any) {
    if (res && props.responseKey && res[props.responseKey]) {
      const values = props.responseKey
        .trim()
        .split(".")
        .reduce((a, b) => a[b], res);

      if (Array.isArray(values) && values.length > 0) {
        const item = values[0];
        // Validate the item has required valueKey
        if (item && item[props.valueKey] != null) {
          return item;
        }
      }
    } else if (res && !props.responseKey) {
      // If no responseKey provided, assume entire response is the item
      if (res && res[props.valueKey] != null) {
        return res;
      }
    }

    return null;
  },
});

const options = computed(() => {
  // Create a map to track unique items by valueKey
  if (props.options && Array.isArray(props.options)) {
    return props.options;
  }
  const uniqueMap = new Map();

  // Process optionList values
  if (optionList.value && Array.isArray(optionList.value)) {
    for (const item of optionList.value) {
      // Only require valueKey to exist
      if (item && item[props.valueKey] != null) {
        uniqueMap.set(item[props.valueKey], item);
      }
    }
  }

  // Add currentDataItem if it exists and isn't already in the map
  if (
    currentDataItem.value &&
    typeof currentDataItem.value === "object" &&
    props.valueKey in currentDataItem.value &&
    (currentDataItem.value as any)[props.valueKey] != null
  ) {
    uniqueMap.set(
      (currentDataItem.value as any)[props.valueKey],
      currentDataItem.value,
    );
  }

  // Convert map values back to array - include all items that have a valueKey
  const validOptions = Array.from(uniqueMap.values()).filter((item) => {
    return item && item[props.valueKey] != null;
  });

  return validOptions;
});

const currentValueItem = computed(() => {
  if (!value.value || value.value === null || value.value === undefined) {
    return null;
  }
  return options.value.find((item) => item[props.valueKey] == value.value);
});

const { get: auxOptionComponent, exists: auxOptionComponentExists } =
  useAuxInput(props.auxInputType ?? "", "dropdownOption");

const { get: auxSelectedComponent, exists: auxSelectedComponentExists } =
  useAuxInput(props.auxInputType ?? "", "dropdownSelected");

// Extra validation specifically for PrimeVue to prevent label errors
const validatedOptions = computed(() => {
  // If using custom auxInputType components, return original options without transformation
  // since custom components handle their own display logic
  if (props.auxInputType && auxOptionComponentExists.value) {
    return options.value;
  }

  const result = options.value.map((item) => {
    let displayValue;

    // Check if displayKey contains slug tokens (e.g., {name} or {user.name|Default})
    if (props.displayKey.includes("{")) {
      try {
        displayValue = processSlugs(props.displayKey, item);
      } catch (e) {
        // Error processing slug
        displayValue = null;
      }
    } else {
      // Standard property path resolution
      try {
        displayValue = props.displayKey
          .trim()
          .split(".")
          .reduce((a, b) => a?.[b], item);
      } catch (e) {
        // Error accessing displayKey
        displayValue = null;
      }
    }

    // If display value is not a string or is empty, use fallback with valueKey
    if (
      typeof displayValue !== "string" ||
      displayValue === null ||
      displayValue === undefined ||
      displayValue === ""
    ) {
      displayValue = `(unknown ${item[props.valueKey]})`;
    }

    // Return item with guaranteed string display property
    return {
      [props.displayKey]: displayValue,
      ...item,
    };
  });

  if (result.length === 0 && options.value.length > 0) {
    // All options were filtered out
  }

  return result;
});

// Handle default values separately to avoid reactive loops
watch(
  validatedOptions,
  (newOptions) => {
    if (newOptions.length > 0) {
      checkDefaultValues(newOptions);
    }
  },
  { immediate: true },
);

const pending = computed(
  () =>
    optionListStatus.value === "pending" ||
    currentDataItemStatus.value === "pending",
);

function syncDropdownValue(targetValue: any) {
  try {
    if (!targetValue) {
      dropdownValue.value = null;
      return;
    }

    if (!validatedOptions.value || !Array.isArray(validatedOptions.value)) {
      return;
    }

    const matchingOption = validatedOptions.value.find((val) => {
      return val && val[props.valueKey] == targetValue;
    });

    if (matchingOption) {
      // With optionValue, PrimeSelect expects the primitive value, not the object
      dropdownValue.value = targetValue;
      actualValue.value = matchingOption[props.displayKey];
      if (form.value) {
        form.value[`${props.dataKey}_Value`] = matchingOption;
      }
    } else if (
      !pending.value &&
      processedEndpoint.value &&
      !processedEndpoint.value.includes("undefined")
    ) {
      // If no matching option and not loading, try to fetch the specific item
      refreshCurrentDataItem();
    }
  } catch (error) {
    // Error in syncDropdownValue
  }
}

watch(
  () => value.value,
  (newValue, oldValue) => {
    try {
      if (
        !props.endpoint ||
        (props.endpoint && props.endpoint.includes("undefined"))
      ) {
        return;
      }
      if (newValue == null) {
        dropdownValue.value = null;
        return;
      }
      if (isEqual(newValue, oldValue)) return;

      syncDropdownValue(newValue);
    } catch (error) {
      // Error in value watcher
    }
  },
);

onMounted(() => {
  try {
    if (
      (!props.endpoint ||
        (props.endpoint && props.endpoint.includes("undefined"))) &&
      !props.options
    ) {
      return;
    }
    if (!props.options && props.defaultExpression) {
      refreshOptionList();
    }

    // Sync dropdown value if we already have a value
    if (value.value) {
      nextTick(() => {
        syncDropdownValue(value.value);
      });
    }

    // Set to false after mount to prevent future default value checks
    nextTick(() => {
      if (!props.options && props.defaultExpression) {
        isInitialLoad.value = false;
      }
    });
  } catch (error) {
    // Error in onMounted
  }
});

const hasErrors: ComputedRef<boolean> = computed(() => {
  if (props.error && props.error !== "") {
    return true;
  }
  return false;
});

const emits = defineEmits<{
  change: [event: IInputEvent];
  input: [event: IChangeEvent];
  blur: [event: IBlurEvent];
}>();

function changeEventHandler(event: any) {
  try {
    const newValue = event.value;

    // Prevent unnecessary updates
    if (isEqual(value.value, newValue)) {
      return;
    }

    // Find the full object from options for setAffectedValues
    const fullOption = newValue
      ? validatedOptions.value?.find((opt) => opt[props.valueKey] == newValue)
      : null;

    setAffectedValues(fullOption ?? {});
    if (newValue && fullOption) {
      value.value = newValue;

      // Use processSlugs if displayKey contains tokens, otherwise use property path
      if (props.displayKey.includes("{")) {
        actualValue.value = processSlugs(props.displayKey, fullOption);
      } else {
        actualValue.value = props.displayKey
          .trim()
          .split(".")
          .reduce((a, b) => a[b], fullOption);
      }

      if (form.value) {
        form.value[`${props.dataKey}_Value`] = fullOption;
      }
    } else {
      value.value = null;
      actualValue.value = null;
      if (form.value) {
        form.value[`${props.dataKey}_Value`] = null;
      }
    }
    emits("change", { ...event, value: value.value });
  } catch (error) {
    // Error in changeEventHandler
  }
}
function inputEventHandler(event: any) {
  emits("input", { ...event, value: value.value });
}
function blurEventHandler(event: any) {
  emits("blur", { ...event, value: value.value });
}

watch(
  () => options.value,
  (newValue, oldValue) => {
    try {
      if (!value.value || !newValue?.length) {
        return;
      }

      // Only update if options actually changed
      if (isEqual(newValue, oldValue)) {
        return;
      }

      // Sync the dropdown value when options change
      syncDropdownValue(value.value);
    } catch (error) {
      // Error in options watcher
    }
  },
);
</script>

<style scoped></style>
